import { ChevronDown, Search, X } from 'lucide-react';
import { PopoverTrigger } from './popover';
import { cn } from '@/lib/utils';
import { fieldFocus, fieldSize, fieldSurface } from './field-surface';

/**
 * Shared building blocks for the popover-based select components
 * (SelectPopover + the data-fetching selectors in components/selectors).
 *
 * Keeping the trigger and dropdown search field here means every select
 * shares one visual language — and the `form` / `toolbar` variants make the
 * single difference between a form field and a list-page filter explicit
 * instead of copy-pasted className strings.
 */

export type SelectTriggerVariant = 'form' | 'toolbar';

const TRIGGER_VARIANT: Record<SelectTriggerVariant, string> = {
	/**
	 * Default look used inside forms/modals — the same field surface as Input, so
	 * a select and a text field sitting side by side are the same control. This
	 * used to claim it matched Input while being `bg-transparent`, which is how a
	 * form ended up with filled inputs and hollow selects.
	 */
	form: `${fieldSurface} ${fieldSize} ${fieldFocus} hover:border-line-3`,
	/** Filled 28px look for list-page filter toolbars — matches SearchInput/ViewToggle. */
	toolbar: 'h-7 border-line-2 bg-surface-input text-xs hover:border-line-3',
};

interface SelectTriggerProps {
	/** Resolved display text; when falsy the muted placeholder shows instead. */
	label?: string | null;
	placeholder?: string;
	/** Whether a value is selected — drives the muted placeholder styling. */
	hasValue: boolean;
	variant?: SelectTriggerVariant;
	disabled?: boolean;
	/** Renders a clear (X) button before the chevron (single-select). */
	onClear?: (e: React.MouseEvent) => void;
	/** Renders a count badge before the chevron (multi-select). */
	count?: number;
	/** Extra classes merged into the trigger (e.g. width on a toolbar). */
	className?: string;
}

export function SelectTrigger({
	label,
	placeholder,
	hasValue,
	variant = 'form',
	disabled = false,
	onClear,
	count,
	className,
}: SelectTriggerProps) {
	return (
		<PopoverTrigger
			disabled={disabled}
			className={cn(
				/* transition-colors stays here, not only in fieldSurface: the toolbar
				   variant does not use the field recipe and still animates its hover. */
				'flex w-full cursor-pointer items-center justify-between rounded-lg border px-2.5 outline-none transition-colors',
				TRIGGER_VARIANT[variant],
				!hasValue && 'text-muted-foreground',
				disabled && 'cursor-not-allowed opacity-50',
				className
			)}
		>
			<span className="truncate">{label || placeholder}</span>
			<div className="flex items-center gap-1">
				{onClear && hasValue && (
					<span
						onClick={onClear}
						className="flex h-4 w-4 items-center justify-center rounded-full text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
					>
						<X size={12} />
					</span>
				)}
				{count != null && count > 0 && (
					<span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 font-mono text-2xs font-semibold text-accent-fg">
						{count}
					</span>
				)}
				<ChevronDown size={14} className="shrink-0 text-muted-foreground" />
			</div>
		</PopoverTrigger>
	);
}

interface SelectSearchInputProps {
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
}

/** The search field shown at the top of a select dropdown. */
export function SelectSearchInput({
	value,
	onChange,
	placeholder = 'Search…',
}: SelectSearchInputProps) {
	return (
		<div className="border-b border-line-1 p-2">
			<div className="relative">
				<Search
					size={12}
					className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-4"
				/>
				<input
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className="h-7 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-8 pr-2 text-xs text-text-1 outline-none placeholder:text-text-4"
					autoFocus
				/>
			</div>
		</div>
	);
}
